import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { CommonPage } from '../../common-page/common-page';
import { ModalController, ToastController } from '@ionic/angular';
import { AlarmoState } from 'src/graphql/generated/schema';
import { from, of, take, takeUntil, catchError, distinctUntilChanged } from 'rxjs';
import { Haptics,  NotificationType } from '@capacitor/haptics';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { actionId, actionType, FRONT_DOOR_LOCK, FRONT_DOOR_LOCK_JAMMED } from 'src/mqtt/actions-list';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { PendingToggleGate } from 'src/app/shared/helpers/pending-toggle-gate';
import { cardBackdrop, cardPop } from 'src/app/shared/animations/expand-fade.animation';

export type LockState = 'locked' | 'unlocked' | 'locking' | 'unlocking' | 'jammed';

// getMessageListener re-emits every entity on any unrelated update, so a report
// counts as new only when its state or timestamp moved.
const sameReport = (a: IShadowState | undefined, b: IShadowState | undefined): boolean =>
  a?.reported?.state === b?.reported?.state && a?.reported?.last_updated === b?.reported?.last_updated;

/** When the unit says a report's state began, on the unit's own clock. */
const reportedAt = (reported: IShadowState['reported'], field: 'last_changed' | 'last_updated'): number => {
  const at = Date.parse(reported[field] ?? reported.last_updated ?? '');
  return Number.isNaN(at) ? Date.now() : at;
};

// Long enough for the bolt to throw and the lock's Z-Wave report to come back.
const LOCK_CONFIRM_TIMEOUT_MS = 15000;

const JAMMED_TOAST = "The door jammed. Make sure it's fully closed, then tap to try again.";
const STILL_JAMMED_TOAST = 'The door is still jammed. Check that nothing is blocking the bolt.';

@Component({
  selector: 'arm-slider',
  templateUrl: './arm-slider.component.html',
  styleUrls: ['./arm-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The app's confirm card, shared with Check on Me (theme/_confirm-card.scss).
  animations: [cardBackdrop, cardPop],
})
export class ArmSliderComponent extends CommonPage implements OnInit, AfterViewInit {
  // FIXME: remove CommonPage extension
  public ARMING_LABEL: string = 'See you soon!';
  public DISARMING_LABEL: string = 'Welcome home!';

  public TO_LEAVE_LABEL: string = 'Slide to leave';
  public TO_ARRIVE_LABEL: string = 'Slide to arrive';
  public CANCEL_ARMING_LABEL: string = 'Slide to cancel';

  // Script entity IDs
  private SCRIPT_ARM_AWAY = 'script.alarm_set_to_armed_away';
  private SCRIPT_DISARM = 'script.alarm_set_to_disarmed';

  @Output() alarmoStateChangeEvent: EventEmitter<AlarmoState> = new EventEmitter();

  @ViewChild('slider', { static: false })
  slider!: ElementRef;

  public armState: AlarmoState | null = null;
  public currentDisplayLabel: string = this.TO_LEAVE_LABEL;
  public isArmingInProgress: boolean = false;

  public minValue: number = 0;
  public maxValue: number = 100;
  public initialValue: number = this.maxValue;
  public showAnimation: boolean = false;
  public animationDuration: number = 3; 

  private currentValue: number = 0;
  private speed: number = 10;
  private animFrameID: number = 0;
  private labelTimeoutId: any = null;
  private lastMqttState: string | null = null;
  private waitingForMqttConfirmation: boolean = false;
  private previousArmState: AlarmoState | null = null;
  private previousDisplayLabel: string = '';
  private pendingStateChange: boolean = false;

  /** Reachability for control; gates arm/disarm so they don't silently no-op. */
  public controlAvailability: 'ready' | 'reconnecting' | 'needs_reauth' = 'reconnecting';

  /**
   * Front door lock (VI-882). Lock never touches the alarm. Any unlock disarms
   * on the unit ("Lock - Unlock Event"), so Unlock is offered only when the unit
   * itself last reported the alarm disarmed (see canUnlock). Otherwise "Slide to
   * arrive", behind the PIN, is the one way to unlock.
   * null = no lock on this unit, or it is unavailable; the button is hidden.
   */
  public lockState: LockState | null = null;
  private readonly lockGate = new PendingToggleGate(LOCK_CONFIRM_TIMEOUT_MS);
  /** The lock's latest settled report, which a failed command falls back to. */
  private lastSettledLock: LockState | null = null;
  /**
   * When the jam sensor last turned on, or null while it is off. Many Z-Wave
   * locks never send the "jam cleared" report, so the sensor can stay on long
   * after the jam is gone: it counts only until the lock next reports locked
   * (see doorJammed).
   */
  private jamSensorOnAt: number | null = null;
  /** When the lock last reported locked. */
  private lastLockedAt = 0;
  /** The pending Lock is a retry of a jammed door. */
  private retryingJam = false;
  /** The open confirm card: unlocking the door, or arming over a jam. */
  public confirm: 'unlock' | 'armOverJam' | null = null;
  /**
   * The alarm state the unit last reported live. Unlike armState it is never
   * optimistic, never left behind by a failed publish, and never restored from
   * the device cache (up to 3 days old). null until a live report arrives.
   */
  private liveAlarmState: string | null = null;

  // The card's Cancel takes focus on open, the safe choice for a keyboard or
  // screen reader user.
  @ViewChild('confirmCancel') set confirmCancel(button: ElementRef<HTMLButtonElement> | undefined) {
    button?.nativeElement.focus();
  }

  public get confirmingUnlock(): boolean {
    return this.confirm === 'unlock';
  }

  constructor(
    public toastController: ToastController,
    public modalCtrl: ModalController,
    private ref: ChangeDetectorRef,
    private mqtt: MqttConnectorService,
    private analyticsService: AnalyticsService,
    private zone: NgZone,
  ) {
    super(toastController, modalCtrl);
  }

  public ngOnInit(): void {
    this.mqtt.getControlAvailability().pipe(
      takeUntil(this.destroy$),
    ).subscribe((availability) => {
      this.controlAvailability = availability;
      this.ref.markForCheck();
    });

    this.mqtt.getMessageListener(
      `${actionType.alarm}.${actionId.alarmo}`
    ).pipe(takeUntil(this.destroy$)).subscribe((message: IShadowState | undefined) => {
      if (message?.reported?.entity_id && message?.reported?.attributes) {
        // Map the state from MQTT to AlarmoState enum
        const mqttState = message?.reported?.state;
        
        if (mqttState) {
          this.liveAlarmState = message.fromDeviceCache ? null : mqttState;

          // Skip processing if the state hasn't changed
          if (this.lastMqttState === mqttState && !this.pendingStateChange) {
            // The live report confirming a cached state can still change what
            // the lock button offers.
            this.closeMootConfirm();
            this.ref.markForCheck();
            return;
          }
          
          // Update the last state
          this.lastMqttState = mqttState;
          
          // Clear any existing timeouts to prevent UI conflicts
          if (this.labelTimeoutId) {
            clearTimeout(this.labelTimeoutId);
            this.labelTimeoutId = null;
          }

          // We got MQTT confirmation, no longer waiting
          this.waitingForMqttConfirmation = false;
          this.pendingStateChange = false;

          if (mqttState.startsWith('armed')) {
            this.handleArmedState();
          } else if (mqttState === 'disarmed') {
            this.handleDisarmedState();
          } else if (mqttState === 'arming') {
            this.handleArmingState();
          } else {
            this.armState = null;
          }
          
          // Update slider position when we get a state update
          this.updateSliderPosition();
          this.closeMootConfirm();
          
          this.alarmoStateChangeEvent.emit(this.armState || AlarmoState.Disarmed);
          this.ref.markForCheck();
        }
      }
    });

    this.mqtt.getMessageListener(FRONT_DOOR_LOCK).pipe(
      distinctUntilChanged(sameReport),
      takeUntil(this.destroy$),
    ).subscribe((message: IShadowState | undefined) => {
      const reported = message?.reported?.state;
      if (!reported) return;

      const next = this.toLockState(reported);
      if (next !== 'locking' && next !== 'unlocking') this.lastSettledLock = next;
      if (next === 'locked') this.lastLockedAt = reportedAt(message.reported, 'last_updated');

      // A new jam report (repeats are filtered above) answers a pending command:
      // show it now rather than holding "Locking…" until the timeout.
      if (next === 'jammed') {
        const answered = this.lockGate.isPending;
        this.lockGate.cancel();
        this.applyLockState('jammed');
        if (answered) this.explainJam();
        return;
      }

      // While a command is pending only locked or unlocked can settle it. The
      // in-between reports say what the optimistic label already does, and an
      // unavailable lock must time out as a failure, not count as unlocked.
      if (this.lockGate.isPending && next !== 'locked' && next !== 'unlocked') return;

      if (this.lockGate.shouldApply(next === 'locked')) {
        this.applyLockState(next);
      }
    });

    this.mqtt.getMessageListener(FRONT_DOOR_LOCK_JAMMED).pipe(
      distinctUntilChanged(sameReport),
      takeUntil(this.destroy$),
    ).subscribe((message: IShadowState | undefined) => {
      const reported = message?.reported?.state;
      if (!reported) return;

      const wasJammed = this.doorJammed;
      this.jamSensorOnAt = reported === 'on' ? reportedAt(message.reported, 'last_changed') : null;

      // As with the lock's own jam report: a new jam answers a pending command.
      if (!wasJammed && this.doorJammed && this.lockGate.isPending) {
        this.lockGate.cancel();
        this.lockState = this.lastSettledLock;
        this.explainJam();
      }
      this.closeMootConfirm();
      this.ref.markForCheck();
    });
  }

  public override ngOnDestroy(): void {
    this.lockGate.cancel();
    super.ngOnDestroy();
  }

  private applyLockState(state: LockState | null): void {
    this.lockState = state;
    this.closeMootConfirm();
    this.ref.markForCheck();
  }

  private explainJam(): void {
    this.presentToast(this.retryingJam ? STILL_JAMMED_TOAST : JAMMED_TOAST);
  }

  /**
   * The lock reports jammed, or its jam sensor turned on after the lock last
   * reported locked. Only a locked report clears a sensor jam: a jammed lock
   * can still report unlocked, and that is exactly the door to warn about.
   */
  public get doorJammed(): boolean {
    if (this.lockState === null) return false;
    return this.lockState === 'jammed'
      || (this.jamSensorOnAt !== null && this.jamSensorOnAt > this.lastLockedAt);
  }

  /** What the button shows: a pending command first, then any jam. */
  public get lockView(): LockState | null {
    if (this.lockState === 'locking' || this.lockState === 'unlocking') return this.lockState;
    return this.doorJammed ? 'jammed' : this.lockState;
  }

  private toLockState(reported: string): LockState | null {
    switch (reported) {
      case 'locked':
        return 'locked';
      case 'jammed':
        return 'jammed';
      case 'locking':
        return 'locking';
      case 'unlocking':
        return 'unlocking';
      case 'unlocked':
      case 'open':
      case 'opening':
        return 'unlocked';
      default:
        return null;
    }
  }

  public get lockLabel(): string {
    switch (this.lockView) {
      case 'locked':
        return 'Unlock door';
      case 'locking':
        return 'Locking…';
      case 'unlocking':
        return 'Unlocking…';
      case 'jammed':
        return 'Door jammed';
      default:
        return 'Lock door';
    }
  }

  /**
   * Disarmed only. Arming or armed, the resident is leaving or gone and the
   * auto-lock secures the door, so there is nothing to offer, except on a jam:
   * then the door may be open, and the button shows in every alarm state so
   * the resident can retry. Locking never touches the alarm. Unlock shows only
   * when canUnlock().
   */
  public get showLockButton(): boolean {
    if (this.lockState === null) return false;
    // A pending command keeps its button, even if the alarm moved on meanwhile.
    if (this.doorJammed || this.lockGate.isPending) return true;
    if (this.lockState === 'locked') return this.canUnlock();
    return this.isDisarmed();
  }

  public onLockButton(): void {
    if (this.lockView === 'locked') {
      this.unlockDoor();
    } else {
      this.lockDoor();
    }
  }

  /** Lock the front door without touching the alarm. A jam retries the lock. */
  public lockDoor(): void {
    if (this.lockView !== 'unlocked' && this.lockView !== 'jammed') return;
    if (!this.showLockButton) return;
    if (!this.isControllable('door')) return;

    this.analyticsService.trackWidgetInteraction('arm_slider', 'lock_initiated');
    this.sendLock(true);
  }

  /**
   * Ask before unlocking: one stray tap must not open the front door. Disarmed
   * only, so the unit's unlock-disarms automation is a no-op.
   */
  public unlockDoor(): void {
    if (this.confirm || !this.canUnlock()) return;
    if (!this.isControllable('door')) return;

    this.confirm = 'unlock';
    this.ref.markForCheck();
  }

  public cancelConfirm(): void {
    // Arming over a jam was asked with the slider at the far end: send it back.
    if (this.confirm === 'armOverJam') this.snapSliderBack();
    this.confirm = null;
    this.ref.markForCheck();
  }

  /** Android hardware Back closes the card, as it would a native alert. */
  @HostListener('document:ionBackButton', ['$event'])
  public onHardwareBack(event: CustomEvent<{ register: (priority: number, handler: () => void) => void }>): void {
    if (!this.confirm) return;
    // Above Ionic's own overlays (100) and router back (0). Ionic calls the
    // handler outside Angular's zone, so re-enter it or the card never redraws.
    event.detail.register(101, () => this.zone.run(() => this.cancelConfirm()));
  }

  public confirmUnlock(): void {
    // Only from an open confirm, so a second tap on it sends nothing.
    if (this.confirm !== 'unlock') return;
    this.confirm = null;
    this.ref.markForCheck();
    if (!this.canUnlock() || !this.isControllable('door')) return;

    this.analyticsService.trackWidgetInteraction('arm_slider', 'unlock_initiated');
    this.sendLock(false);
  }

  public confirmArmOverJam(): void {
    if (this.confirm !== 'armOverJam') return;
    this.confirm = null;
    this.ref.markForCheck();
    if (!this.isDisarmed()) return;

    this.analyticsService.trackWidgetInteraction('arm_slider', 'arm_over_jam');
    this.doLock(true);
  }

  /**
   * Both the widget and the unit's own live report must say disarmed. armState
   * alone can be optimistic (mid slide-to-arrive), stuck after a failed disarm
   * publish, or restored from the device cache, and an Unlock sent while the
   * alarm is really armed would disarm it with no PIN. A jammed door is only
   * ever retried as a lock.
   */
  private canUnlock(): boolean {
    return this.lockState === 'locked' && !this.doorJammed
      && this.isDisarmed() && this.liveAlarmState === 'disarmed';
  }

  /** Close the confirm if the alarm or the lock moved on while it was open. */
  private closeMootConfirm(): void {
    if (this.confirm === 'unlock' && !this.canUnlock()) this.confirm = null;
    // Armed from elsewhere: the slider already moved with the report.
    if (this.confirm === 'armOverJam' && !this.isDisarmed()) this.confirm = null;
  }

  private sendLock(lock: boolean): void {
    this.triggerHapticFeedback();

    let unreachable = false;
    this.retryingJam = lock && this.lockView === 'jammed';
    this.lockState = lock ? 'locking' : 'unlocking';
    // The gate's own fallback value (!lock) goes unused: the revert below reads
    // lastSettledLock, which can also be 'jammed' or null.
    const token = this.lockGate.begin(lock, !lock, () => {
      // The latest report, not the state before the tap: the lock may have
      // moved while the command was pending.
      this.lockState = this.lastSettledLock;
      const verb = lock ? 'locked' : 'unlocked';
      this.presentToast(unreachable
        ? `Couldn't reach your home. The door was not ${verb}.`
        : this.retryingJam
          ? STILL_JAMMED_TOAST
          : lock
            ? "The door didn't lock. Check that it's fully closed, then try again."
            : "The door didn't unlock. Try again, or use your keypad code.");
      this.ref.markForCheck();
    });

    this.mqtt.callHaAction(actionType.lock, lock ? 'lock' : 'unlock', { entity_id: FRONT_DOOR_LOCK }).pipe(
      take(1),
    ).subscribe({
      error: () => {
        unreachable = true;
        this.lockGate.failIfCurrent(token);
      },
    });
    this.ref.markForCheck();
  }

  private handleArmedState(): void {
    this.armState = AlarmoState.Armed;
    this.initialValue = this.minValue; // Slider on left
    this.currentDisplayLabel = this.TO_ARRIVE_LABEL;
    this.showAnimation = false;
  }

  private handleDisarmedState(): void {
    this.armState = AlarmoState.Disarmed;
    this.initialValue = this.maxValue; // Slider on right
    this.currentDisplayLabel = this.TO_LEAVE_LABEL;
    this.showAnimation = false;
  }

  private handleArmingState(): void {
    this.armState = AlarmoState.Arming;
    this.initialValue = this.minValue; // Slider on left for arming state
    
    // Set the correct label immediately - 'See you soon!'
    this.currentDisplayLabel = this.ARMING_LABEL;
    
    // Also update label to indicate that sliding can cancel arming
    this.labelTimeoutId = setTimeout(() => {
      if (this.armState === AlarmoState.Arming) {
        this.currentDisplayLabel = this.CANCEL_ARMING_LABEL;
        this.ref.markForCheck();
      }
    }, 1000); // Show cancel option after 1 second
    
    // Trigger blinking animation
    this.runBlinking();
  }

  private updateSliderPosition(): void {
    if (this.slider?.nativeElement) {
      // Set slider to the proper position based on state
      this.slider.nativeElement.value = this.initialValue;
      this.currentValue = this.initialValue;
      
      // Cancel any ongoing animation
      if (this.animFrameID) {
        window.cancelAnimationFrame(this.animFrameID);
        this.animFrameID = 0;
      }
    }
  }

  private revertToPreviousState(): void {
    if (this.pendingStateChange && this.previousArmState !== null) {
      this.armState = this.previousArmState;
      this.currentDisplayLabel = this.previousDisplayLabel;
      
      // Update the slider position based on previous state
      this.initialValue = this.isArmed() ? this.minValue : this.maxValue;
      this.updateSliderPosition();
      
      this.alarmoStateChangeEvent.emit(this.armState);
      this.pendingStateChange = false;
      this.ref.markForCheck();
    } else {
      // Default to disarmed if no previous state
      this.handleDisarmedState();
    }
    this.closeMootConfirm();
  }

  public ngAfterViewInit(): void {
    this.initHandlers(this.slider.nativeElement);
  }

  public isArmed() {
    return this.armState === AlarmoState.Armed || this.armState === AlarmoState.Arming;
  }

  public isDisarmed(): boolean {
    return this.armState === AlarmoState.Disarmed;
  }

  public isArming() {
    return this.armState === AlarmoState.Arming;
  }

  private triggerHapticFeedback() {
    try {
      // Attempt to use haptic feedback, but catch any errors
      Haptics.notification({ type: NotificationType.Success });
    } catch (error) {
      // Ignore if haptics not available
    }
  }

  /** Gate arm/disarm when the home is unreachable; snap the slider back. */
  private ensureControllable(): boolean {
    if (this.isControllable()) return true;
    this.animFrameID = window.requestAnimationFrame(this.animateHandler.bind(this));
    return false;
  }

  /** False, with a toast saying why, when the home can't take a command. */
  private isControllable(what: 'alarm' | 'door' = 'alarm'): boolean {
    if (this.controlAvailability === 'ready') return true;
    this.presentToast(
      this.controlAvailability === 'needs_reauth'
        ? `Please sign in again to control your ${what}.`
        : 'Reconnecting to your home. Try again in a moment.',
    );
    return false;
  }

  private snapSliderBack(): void {
    this.animFrameID = window.requestAnimationFrame(this.animateHandler.bind(this));
  }

  private doLock(overJam = false) {
    if (!this.ensureControllable()) return;

    // Arming walks the resident away from a door that may not be locked.
    if (this.doorJammed && !overJam) {
      this.confirm = 'armOverJam';
      this.ref.markForCheck();
      return;
    }

    // Trigger haptic feedback if available
    this.triggerHapticFeedback();

    // Track arm initiated
    this.analyticsService.trackWidgetInteraction('arm_slider', 'arm_initiated');

    // Store the current state before changing
    this.previousArmState = this.armState;
    this.previousDisplayLabel = this.currentDisplayLabel;
    this.pendingStateChange = true;

    // Proceed with modal and arming
    from(this.openModal()).subscribe((pinPassed: boolean) => {
      if (pinPassed) {
        // Track successful arm (PIN passed)
        this.analyticsService.trackWidgetInteraction('arm_slider', 'armed');

        // Call the script to arm the system via MQTT
        this.mqtt.setStateValue(this.SCRIPT_ARM_AWAY, 'on').pipe(
          take(1),
          catchError(() => {
            this.pendingStateChange = false;
            return of(false);
          })
        ).subscribe((res) => {
          if (res === false) {
            // Publish never reached the home: do NOT show "Arming" (false sense
            // of security on an alarm). Revert and tell the resident.
            this.presentToast("Couldn't reach your home. The alarm was not armed.");
            this.revertToPreviousState();
            this.ref.markForCheck();
            return;
          }
          // Update UI immediately for responsive experience
          this.initialValue = this.minValue; // Slider to left
          this.armState = AlarmoState.Arming; // Set to Arming, not Armed right away
          this.currentDisplayLabel = this.ARMING_LABEL;
          this.runBlinking();
          this.alarmoStateChangeEvent.emit(this.armState);
          
          // Also update our MQTT state tracking to prevent duplicate processing
          this.lastMqttState = 'arming';
          this.waitingForMqttConfirmation = true;
          
          // Update the UI immediately
          this.updateSliderPosition();
          
          // After initial message, show the cancel option
          if (this.labelTimeoutId) {
            clearTimeout(this.labelTimeoutId);
          }
          
          this.labelTimeoutId = setTimeout(() => {
            if (this.armState === AlarmoState.Arming) {
              this.currentDisplayLabel = this.CANCEL_ARMING_LABEL;
              this.ref.markForCheck();
            }
          }, 1000); // Show cancel option after 1 second
          
          // Revert to previous state if no MQTT confirmation after timeout
          setTimeout(() => {
            // Only do this if we never received an MQTT confirmation
            if (this.waitingForMqttConfirmation) {
              this.revertToPreviousState();
            }
          }, this.animationDuration * 1000);
          
          this.ref.markForCheck();
        });
      } else {
        this.pendingStateChange = false;
        this.animFrameID = window.requestAnimationFrame(this.animateHandler.bind(this));
      }
    });
  }

  private doUnlock() {
    if (!this.ensureControllable()) return;

    // Trigger haptic feedback if available
    this.triggerHapticFeedback();

    // Track disarm initiated
    this.analyticsService.trackWidgetInteraction('arm_slider', 'disarm_initiated');

    // Store the current state before changing
    this.previousArmState = this.armState;
    this.previousDisplayLabel = this.currentDisplayLabel;
    this.pendingStateChange = true;

    // Proceed with modal and disarming
    from(this.openModal()).subscribe((pinPassed: boolean) =>{
      if (pinPassed) {
        // Track successful disarm (PIN passed)
        this.analyticsService.trackWidgetInteraction('arm_slider', 'disarmed');

        // Call the script to disarm the system via MQTT
        this.mqtt.setStateValue(this.SCRIPT_DISARM, 'on').pipe(
          take(1),
          catchError(() => {
            this.pendingStateChange = false;
            return of(false);
          })
        ).subscribe((res) => {
          if (res === false) {
            // Publish never reached the home: don't show "Disarming". Revert.
            this.presentToast("Couldn't reach your home. The alarm was not disarmed.");
            this.revertToPreviousState();
            this.ref.markForCheck();
            return;
          }
          // Update UI immediately for responsive experience
          this.initialValue = this.maxValue; // Slider to right
          this.armState = AlarmoState.Disarmed;
          this.currentDisplayLabel = this.DISARMING_LABEL;
          this.runBlinking();
          this.alarmoStateChangeEvent.emit(this.armState);
          
          // Also update our MQTT state tracking to prevent duplicate processing
          this.lastMqttState = 'disarmed';
          this.waitingForMqttConfirmation = true;
          
          // Update UI immediately
          this.updateSliderPosition();
          
          // Revert to previous state if no MQTT message comes in
          if (this.labelTimeoutId) {
            clearTimeout(this.labelTimeoutId);
          }
          
          this.labelTimeoutId = setTimeout(() => {
            // Only update if no MQTT confirmation was received
            if (this.waitingForMqttConfirmation) {
              this.revertToPreviousState();
            }
          }, this.animationDuration * 1000);
          
          this.ref.markForCheck();
        });
      } else {
        this.pendingStateChange = false;
        this.animFrameID = window.requestAnimationFrame(this.animateHandler.bind(this));
      }
    });
  }

  private runBlinking() {
    // Start the animation
    this.showAnimation = true;
    
    setTimeout(() => {
      this.showAnimation = false;
      this.ref.markForCheck();
    }, this.animationDuration * 1000);
  }

  private initHandlers(nativeSlider: any) {
    nativeSlider.addEventListener('mousedown', this.slideStart.bind(this), false);
    nativeSlider.addEventListener('mousestart', this.slideStart.bind(this), false);
    nativeSlider.addEventListener('mouseup', this.checkActivation.bind(this), false);
    nativeSlider.addEventListener('touchend', this.checkActivation.bind(this), false);
  }

  private slideStart() {
    window.cancelAnimationFrame(this.animFrameID);
    this.currentValue = +this.slider.nativeElement.value;
  }

  private checkActivation(_event: TouchEvent) {
    this.currentValue = +this.slider?.nativeElement?.value;
    
    // When disarmed and sliding to left - lock
    if (this.armState === AlarmoState.Disarmed && this.currentValue <= this.minValue) {
      this.doLock();
    }
    
    // When armed OR arming, allow sliding to the right to disarm/cancel
    if ((this.armState === AlarmoState.Armed || this.armState === AlarmoState.Arming) 
        && this.currentValue >= this.maxValue) {
      this.doUnlock();
    }
    
    if (this.currentValue > this.minValue && this.currentValue < this.maxValue) {
      this.animFrameID = window.requestAnimationFrame(this.animateHandler.bind(this));
    }
  }

  private animateHandler() {
    this.slider.nativeElement.value = this.currentValue;

    if ((this.isArmed() && this.currentValue > this.minValue) || (!this.isArmed() && this.currentValue < this.maxValue)) {
    	window.requestAnimationFrame(this.animateHandler.bind(this));
    }

    this.currentValue = this.isArmed() ? this.currentValue - this.speed : this.currentValue + this.speed;
  }
}
